// The live view of the simulated assessment, shown above the disc while a run
// is in flight and kept (collapsed to a summary) after it finishes.
//
// It narrates what the pipeline is doing in a teacher's words rather than a
// log: a strip of the students (waiting / assessing / assessed), the student
// being assessed right now with the four stages the real pipeline goes
// through — read the files → understand what was made → find the skill
// developmental descriptors the work gives evidence for → score them against
// the rubric — the files being looked at, and one plain sentence per stage.
// Everything shown comes from the fixtures (real file names, real descriptor
// counts); nothing is invented.
import { Avatar, Badge, Box, Group, Loader, Paper, Progress, Stack, Stepper, Text, ThemeIcon, Tooltip } from '@mantine/core'
import {
  IconBrain,
  IconCheck,
  IconFileText,
  IconListSearch,
  IconPhoto,
  IconPresentation,
  IconScale,
  IconSparkles,
  IconTable,
  IconVideo,
} from '@tabler/icons-react'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'
import { ARC_AI_GRADIENT } from 'themes/ArcTheme'
import { projectFixtures } from '../data/fixtures'
import { useProjectDemoState } from '../data/demoState'
import { getProgress, resumeIfRunning, subscribeProgress } from '../data/simulation'
import type { Phase, StageFile, StageStatus, StudentProgress } from '../data/simulation'

const PHASES: Phase[] = ['reading', 'understanding', 'matching', 'scoring']
const PHASE_ICON: Record<Phase, JSX.Element> = {
  reading: <IconFileText size={14} />,
  understanding: <IconBrain size={14} />,
  matching: <IconListSearch size={14} />,
  scoring: <IconScale size={14} />,
  done: <IconCheck size={14} />,
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

// File chips: the icon says what kind of thing the student handed in
// (photo, document, spreadsheet, slides, video) — the mix is part of the story.
function fileIcon(f: StageFile) {
  const n = f.name.toLowerCase()
  if (f.kind === 'image') return <IconPhoto size={13} />
  if (f.kind === 'video') return <IconVideo size={13} />
  if (/\.(xlsx|xls|csv)$/.test(n)) return <IconTable size={13} />
  if (/\.(pptx|ppt|key)$/.test(n)) return <IconPresentation size={13} />
  return <IconFileText size={13} />
}

function StudentStrip({ students }: { students: StudentProgress[] }) {
  const { t } = useTranslation()
  return (
    <Group spacing={8}>
      {students.map(s => {
        const done = s.state === 'done'
        const active = s.state === 'active'
        return (
          <Tooltip
            key={s.studentId}
            label={`${s.studentName} · ${t(`arcdisk_demo.student_${s.state}`)}`}
            withArrow
            position='top'
          >
            <Group
              spacing={6}
              noWrap
              px={8}
              py={4}
              sx={theme => ({
                borderRadius: 999,
                border: `1px solid ${active ? theme.colors.violet[4] : done ? theme.colors.teal[3] : theme.colors.gray[3]}`,
                background: active ? theme.colors.violet[0] : done ? theme.colors.teal[0] : theme.white,
                opacity: s.state === 'waiting' ? 0.6 : 1,
                transition: 'all 300ms ease',
              })}
            >
              <Avatar
                size={22}
                radius='xl'
                color={done ? 'teal' : active ? 'violet' : 'gray'}
                variant={s.state === 'waiting' ? 'light' : 'filled'}
                styles={{ placeholder: { fontSize: 10 } }}
              >
                {done ? <IconCheck size={12} /> : initialsOf(s.studentName)}
              </Avatar>
              <Text size='xs' weight={active ? 600 : 500}>
                {s.studentName}
              </Text>
              {active && <Loader size={12} color='violet' />}
            </Group>
          </Tooltip>
        )
      })}
    </Group>
  )
}

function CurrentStage({ stage }: { stage: StageStatus }) {
  const { t } = useTranslation()
  const first = stage.studentName.split(/\s+/)[0]
  const phaseIndex = stage.phase === 'done' ? PHASES.length : PHASES.indexOf(stage.phase)
  const detail = t(`arcdisk_demo.stage_${stage.phase}_detail`, {
    name: first,
    count: stage.files.length,
    descriptors: stage.descriptors,
  })

  return (
    <Box
      p='md'
      sx={theme => ({
        borderRadius: theme.radius.md,
        background: theme.white,
        border: `1px solid ${theme.colors.gray[2]}`,
        // Reading shows a row of file chips, the other stages don't; a fixed
        // floor keeps the disc below from jumping as the stages change.
        minHeight: 200,
      })}
    >
      <Group spacing='sm' noWrap mb='sm'>
        <Avatar radius='xl' size={34} variant='gradient' gradient={{ from: '#5B4FEB', to: '#9B8CFF' }}>
          {initialsOf(stage.studentName)}
        </Avatar>
        <div>
          <Text size='xs' color='dimmed' transform='uppercase' sx={{ letterSpacing: 0.4 }}>
            {t('arcdisk_demo.now_assessing')}
          </Text>
          <Text weight={600} size='sm'>
            {stage.studentName}
          </Text>
        </div>
      </Group>

      <Stepper
        active={phaseIndex}
        size='xs'
        iconSize={28}
        color='violet'
        completedIcon={<IconCheck size={14} />}
        breakpoint='sm'
        styles={{
          stepBody: { marginLeft: 8 },
          stepLabel: { fontSize: 12, lineHeight: 1.25 },
          separator: { marginLeft: 6, marginRight: 6 },
        }}
      >
        {PHASES.map(p => (
          <Stepper.Step key={p} label={t(`arcdisk_demo.stage_${p}`)} icon={PHASE_ICON[p]} loading={stage.phase === p} />
        ))}
      </Stepper>

      <Text size='sm' mt='sm' sx={{ minHeight: 22 }}>
        {detail}
      </Text>

      {stage.phase === 'reading' && stage.files.length > 0 && (
        <Group spacing={6} mt='xs'>
          {stage.files.map((f, idx) => {
            const seen = stage.activeFile != null && idx < stage.activeFile
            const looking = stage.activeFile === idx
            return (
              <Badge
                key={`${f.name}-${idx}`}
                size='sm'
                radius='sm'
                variant={looking ? 'filled' : 'outline'}
                color={looking ? 'violet' : seen ? 'teal' : 'gray'}
                leftSection={<Box sx={{ display: 'flex' }}>{seen ? <IconCheck size={12} /> : fileIcon(f)}</Box>}
                sx={{ textTransform: 'none', fontWeight: 500, maxWidth: 260, transition: 'all 250ms ease' }}
              >
                {f.name}
              </Badge>
            )
          })}
        </Group>
      )}
    </Box>
  )
}

export function AssessmentProgress({ adProjectId }: { adProjectId: string }) {
  const { t } = useTranslation()
  const state = useProjectDemoState(adProjectId)
  const progress = useSyncExternalStore(
    cb => subscribeProgress(adProjectId, cb),
    () => getProgress(adProjectId),
  )
  const totalAssessments = projectFixtures[adProjectId]?.grades.length ?? 0

  // A reload mid-run loses the timers; pick the run back up where it stopped.
  useEffect(() => {
    resumeIfRunning(adProjectId)
  }, [adProjectId])

  // One toast when a run finishes while this page is open.
  const prevStatus = useRef(state.status)
  useEffect(() => {
    if (prevStatus.current === 'grading' && state.status === 'graded') {
      notifications.show({
        title: t('arcdisk_demo.progress_done'),
        message: t('arcdisk_demo.complete_notice', { count: totalAssessments }),
        color: 'violet',
        icon: <IconSparkles size={16} />,
      })
    }
    prevStatus.current = state.status
  }, [state.status, totalAssessments, t])

  if (state.status === 'ungraded') return null

  const running = state.status === 'grading'

  return (
    <Paper
      withBorder
      radius='md'
      p='md'
      sx={{
        background: running ? 'linear-gradient(180deg, #F3F1FF 0%, #F7F8FC 100%)' : '#F7F8FC',
        borderColor: running ? '#C9C2FF' : undefined,
      }}
    >
      <Group position='apart' align='center' noWrap>
        <Group spacing='sm' noWrap>
          {running ? (
            <ThemeIcon radius='xl' size={36} sx={{ background: ARC_AI_GRADIENT, border: 0 }}>
              <IconSparkles size={18} />
            </ThemeIcon>
          ) : (
            <ThemeIcon color='teal' radius='xl' size={36}>
              <IconCheck size={18} />
            </ThemeIcon>
          )}
          <div>
            <Text weight={600} size='sm'>
              {running ? t('arcdisk_demo.progress_title') : t('arcdisk_demo.progress_done')}
            </Text>
            <Text size='xs' color='dimmed'>
              {running
                ? t('arcdisk_demo.students_progress', { done: progress.done, total: progress.total })
                : t('arcdisk_demo.run_summary', { students: progress.total, count: totalAssessments })}
            </Text>
          </div>
        </Group>
        <Badge color={running ? 'violet' : 'teal'} variant='light' size='lg' leftSection={<IconSparkles size={12} />}>
          {running ? t('arcdisk_demo.assessing') : t('arcdisk_demo.assessed')}
        </Badge>
      </Group>

      <Progress
        mt='sm'
        value={Math.max(3, Math.round(progress.fraction * 100))}
        color='violet'
        radius='xl'
        size={6}
        striped={running}
        animate={running}
      />

      <Stack spacing='sm' mt='md'>
        {progress.students.length > 0 && <StudentStrip students={progress.students} />}
        {running && progress.stage && <CurrentStage stage={progress.stage} />}
      </Stack>
    </Paper>
  )
}
